// /admin/repartidores — gestión del equipo de reparto.
// Listar, invitar, activar/desactivar, editar zona/vehículo y reenviar link.
//
// Quién puede ver esta página: solo admin (RequireAuth).
// La invitación llama a edge function `invitar-repartidor` que crea el user en
// auth + setea role + inserta en `public.repartidores`. Devuelve magic link que
// el admin copia y pasa por WhatsApp.

import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const FN_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invitar-repartidor`

const fmtFecha = (d) => d
  ? new Date(d).toLocaleDateString('es-UY', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—'

// Fecha relativa: "hace 3 meses" / "hace 2 días" / "hoy"
function fmtFechaRelativa(d) {
  if (!d) return '—'
  const diasAtras = Math.floor((Date.now() - new Date(d).getTime()) / 86400000)
  if (diasAtras < 1) return 'hoy'
  if (diasAtras === 1) return 'ayer'
  if (diasAtras < 30) return `hace ${diasAtras} días`
  const meses = Math.floor(diasAtras / 30)
  if (meses < 12) return `hace ${meses} ${meses === 1 ? 'mes' : 'meses'}`
  const años = Math.floor(diasAtras / 365)
  return `hace ${años} ${años === 1 ? 'año' : 'años'}`
}

const COMBUSTIBLE_LABEL = { nafta: 'Nafta', gasoil: 'Gasoil', ninguno: 'Tercerizado' }

export default function AdminRepartidores() {
  const navigate = useNavigate()
  const [filas, setFilas] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [filtro, setFiltro] = useState('activos') // activos | inactivos | todos
  const [busqueda, setBusqueda] = useState('')

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setLoading(true)
    setError(null)
    try {
      // 1) Todos los repartidores (incluye origen configurable para el modal editar)
      const { data: reps, error: re } = await supabase
        .from('repartidores')
        .select('id, vehiculo, zona, notas, activo, created_at, origen_lat, origen_lng, origen_descripcion, combustible_tipo, km_por_litro_override, precio_litro_override, tarifa_facturada_por_viaje, regresa_a_origen')
        .order('created_at', { ascending: false })
      if (re) throw re

      const ids = (reps || []).map(r => r.id)
      if (ids.length === 0) {
        setFilas([])
        return
      }

      // 2) Datos personales (profiles)
      const { data: profs, error: pe } = await supabase
        .from('profiles')
        .select('id, nombre, email, phone, created_at')
        .in('id', ids)
      if (pe) throw pe
      const pMap = new Map((profs || []).map(p => [p.id, p]))

      // 3) Conteo de entregas por repartidor (histórico + hoy)
      const hoyISO = new Date().toISOString().slice(0, 10)
      const { data: entregas, error: ee } = await supabase
        .from('entregas')
        .select('repartidor_id, estado, fecha_programada')
        .in('repartidor_id', ids)
      if (ee) throw ee

      const conteo = new Map()
      ;(entregas || []).forEach(e => {
        if (!e.repartidor_id) return
        const c = conteo.get(e.repartidor_id) || { total: 0, hoy: 0, entregadas: 0 }
        c.total++
        if (e.fecha_programada === hoyISO) c.hoy++
        if (e.estado === 'entregada') c.entregadas++
        conteo.set(e.repartidor_id, c)
      })

      // 4) KM del mes actual por repartidor, split canal Tienda/ML
      const primerDiaMes = new Date()
      primerDiaMes.setDate(1)
      const primerDiaISO = primerDiaMes.toISOString().slice(0, 10)
      const { data: kms, error: ke } = await supabase
        .from('v_repartidor_km_mensual_por_canal')
        .select('repartidor_id, canal, km_total, entregadas, combustible_tipo, costo_combustible_estimado, costo_facturado_tercero')
        .in('repartidor_id', ids)
        .eq('mes', primerDiaISO)
      if (ke) throw ke
      // Reducir a { repartidor_id: { tienda: {...}, ml: {...} } }
      const kmMap = new Map()
      ;(kms || []).forEach(k => {
        const canal = k.canal === 'mercadolibre' ? 'ml' : 'tienda'
        const acc = kmMap.get(k.repartidor_id) || {
          tienda: { km_total: 0, costo_combustible_estimado: 0, costo_facturado_tercero: 0 },
          ml: { km_total: 0, costo_combustible_estimado: 0, costo_facturado_tercero: 0 },
        }
        acc[canal] = {
          km_total: Number(k.km_total) || 0,
          costo_combustible_estimado: Number(k.costo_combustible_estimado) || 0,
          costo_facturado_tercero: Number(k.costo_facturado_tercero) || 0,
          entregadas: k.entregadas,
        }
        kmMap.set(k.repartidor_id, acc)
      })

      const filasFinal = (reps || []).map(r => {
        const p = pMap.get(r.id) || {}
        const c = conteo.get(r.id) || { total: 0, hoy: 0, entregadas: 0 }
        const km = kmMap.get(r.id) || {
          tienda: { km_total: 0, costo_combustible_estimado: 0, costo_facturado_tercero: 0 },
          ml: { km_total: 0, costo_combustible_estimado: 0, costo_facturado_tercero: 0 },
        }
        const km_mes = (km.tienda.km_total || 0) + (km.ml.km_total || 0)
        const costo_combustible_mes = (km.tienda.costo_combustible_estimado || 0) + (km.ml.costo_combustible_estimado || 0)
        const costo_facturado_mes = (km.tienda.costo_facturado_tercero || 0) + (km.ml.costo_facturado_tercero || 0)
        return {
          id: r.id,
          nombre: p.nombre || '—',
          email: p.email || '',
          phone: p.phone || '',
          vehiculo: r.vehiculo,
          zona: r.zona,
          notas: r.notas,
          activo: r.activo,
          creado: r.created_at,
          origen_lat: r.origen_lat,
          origen_lng: r.origen_lng,
          origen_descripcion: r.origen_descripcion,
          // Config de combustible/tarifa (para render + ModalEditar)
          combustible_tipo: r.combustible_tipo,
          km_por_litro_override: r.km_por_litro_override,
          precio_litro_override: r.precio_litro_override,
          tarifa_facturada_por_viaje: r.tarifa_facturada_por_viaje,
          regresa_a_origen: r.regresa_a_origen,
          stats: c,
          km_mes,
          costo_combustible_mes,
          costo_facturado_mes,
          km_tienda: km.tienda.km_total || 0,
          km_ml: km.ml.km_total || 0,
        }
      })
      setFilas(filasFinal)
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  async function toggleActivo(rep) {
    const accion = rep.activo ? 'desactivar' : 'reactivar'
    if (!confirm(`¿${accion.charAt(0).toUpperCase() + accion.slice(1)} a ${rep.nombre}?`)) return
    const { error } = await supabase
      .from('repartidores')
      .update({ activo: !rep.activo })
      .eq('id', rep.id)
    if (error) { alert('Error: ' + error.message); return }
    cargar()
  }

  const [linkModal, setLinkModal] = useState(null) // {rep, link} cuando se genera link
  const [editRep, setEditRep] = useState(null) // repartidor a editar

  async function reenviarLink(rep) {
    if (!confirm(`Generar nuevo magic link para ${rep.nombre} (${rep.email})?`)) return
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(FN_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: rep.email,
          nombre: rep.nombre,
          telefono: rep.phone,
          vehiculo: rep.vehiculo,
          zona: rep.zona,
        }),
      })
      const j = await res.json()
      if (!res.ok) throw new Error(j.error || 'Error')
      if (j.action_link) {
        setLinkModal({ rep, link: j.action_link })
      } else {
        alert(j.mensaje || 'Link generado pero sin URL')
      }
    } catch (e) {
      alert('Error: ' + (e.message || String(e)))
    }
  }

  const filtradas = useMemo(() => {
    let r = filas
    if (filtro === 'activos') r = r.filter(x => x.activo)
    if (filtro === 'inactivos') r = r.filter(x => !x.activo)
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase()
      r = r.filter(x =>
        x.nombre?.toLowerCase().includes(q) ||
        x.email?.toLowerCase().includes(q) ||
        x.phone?.toLowerCase().includes(q) ||
        x.zona?.toLowerCase().includes(q),
      )
    }
    return r
  }, [filas, filtro, busqueda])

  const counts = useMemo(() => ({
    activos: filas.filter(x => x.activo).length,
    inactivos: filas.filter(x => !x.activo).length,
    todos: filas.length,
  }), [filas])

  return (
    <AdminLayout>
      <div className="page">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 4 }}>
          <h1 style={{ margin: 0 }}>🚐 Repartidores</h1>
          <button onClick={() => setModalOpen(true)} className="btn-primary">
            + Invitar repartidor
          </button>
        </div>
        <p className="muted">El equipo que reparte los kits. Cada repartidor entra con su propio mail y ve solo su ruta del día en <code>/repartidor/hoy</code>.</p>

        {/* Resumen operativo del mes — 1 tile con lo importante */}
        {!loading && filas.length > 0 && (() => {
          const kmMesTotal = filas.reduce((s, r) => s + (r.km_mes || 0), 0)
          const costoCombTotal = filas.reduce((s, r) => s + (r.costo_combustible_mes || 0), 0)
          const costoFactTotal = filas.reduce((s, r) => s + (r.costo_facturado_mes || 0), 0)
          const entregasHoy = filas.reduce((s, r) => s + (r.stats?.hoy || 0), 0)
          return (
            <div className="rep-resumen">
              <div className="rep-resumen-item">
                <div className="rep-resumen-label">Activos</div>
                <div className="rep-resumen-value" style={{ color: '#10B981' }}>{counts.activos}<span className="rep-resumen-sub"> de {counts.todos}</span></div>
              </div>
              <div className="rep-resumen-item">
                <div className="rep-resumen-label">Entregas hoy</div>
                <div className="rep-resumen-value">{entregasHoy}</div>
              </div>
              <div className="rep-resumen-item">
                <div className="rep-resumen-label">Km del mes</div>
                <div className="rep-resumen-value">{kmMesTotal.toFixed(0)}<span className="rep-resumen-sub"> km</span></div>
              </div>
              <div className="rep-resumen-item">
                <div className="rep-resumen-label">Combustible mes</div>
                <div className="rep-resumen-value" style={{ color: '#1a3461' }}>${Math.round(costoCombTotal).toLocaleString('es-UY')}</div>
              </div>
              {costoFactTotal > 0 && (
                <div className="rep-resumen-item">
                  <div className="rep-resumen-label">Facturado tercero</div>
                  <div className="rep-resumen-value" style={{ color: '#b45309' }}>${Math.round(costoFactTotal).toLocaleString('es-UY')}</div>
                </div>
              )}
            </div>
          )
        })()}

        {/* Filtros + buscador */}
        <div className="ap-filtros" style={{ marginBottom: 12 }}>
          {[
            { v: 'activos', label: '🟢 Activos', c: counts.activos },
            { v: 'inactivos', label: '⏸ Inactivos', c: counts.inactivos },
            { v: 'todos', label: 'Todos', c: counts.todos },
          ].map(o => (
            <button key={o.v}
              className={`ap-filtro ${filtro === o.v ? 'is-active' : ''}`}
              onClick={() => setFiltro(o.v)}>
              {o.label} <em>{o.c}</em>
            </button>
          ))}
        </div>

        <input
          type="search"
          placeholder="🔎 Buscar por nombre, email, teléfono o vehículo…"
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          style={{
            width: '100%', padding: '10px 14px', marginBottom: 16,
            border: '1px solid #d1d5db', borderRadius: 10, fontSize: 14,
          }}
        />

        {error && <div className="alert-error">{error}</div>}
        {loading && <div className="ap-loading">Cargando…</div>}

        {!loading && filtradas.length === 0 && (
          <div className="ap-empty">
            <div style={{ fontSize: 48 }}>🚐</div>
            <h3>Sin repartidores en este filtro</h3>
            <p>Empezá invitando al primero desde el botón de arriba.</p>
          </div>
        )}

        {!loading && filtradas.length > 0 && (
          <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
            <table className="rep-table">
              <thead>
                <tr>
                  <th>Repartidor</th>
                  <th>Vehículo</th>
                  <th style={{ textAlign: 'center' }}>Hoy</th>
                  <th style={{ textAlign: 'center' }}>Histórico</th>
                  <th style={{ textAlign: 'right' }}>Km mes</th>
                  <th style={{ textAlign: 'right' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtradas.map(r => {
                  const combLabel = COMBUSTIBLE_LABEL[r.combustible_tipo] || 'Nafta'
                  const kmL = r.km_por_litro_override
                  return (
                  <tr key={r.id} className={`row-link ${r.activo ? '' : 'inactivo'}`}
                      onClick={(e) => {
                        if (e.target.closest('button')) return
                        navigate(`/admin/repartidores/${r.id}`)
                      }}>
                    <td>
                      <div style={{ fontWeight: 700, color: '#1a3461', display: 'flex', alignItems: 'center', gap: 6 }}>
                        {r.nombre}
                        <span style={{ fontSize: 10, color: '#00A8B5', opacity: 0.6 }}>↗</span>
                        {!r.activo && <span style={{ marginLeft: 6, fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>· pausado</span>}
                      </div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>
                        {r.email && <span>📧 {r.email}</span>}
                        {r.email && r.phone && <span style={{ margin: '0 6px', color: '#cbd5e1' }}>·</span>}
                        {r.phone && <span>📱 {r.phone}</span>}
                      </div>
                      <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                        Alta: {fmtFechaRelativa(r.creado)}
                      </div>
                    </td>
                    <td style={{ fontSize: 13, color: '#475569' }}>
                      {r.vehiculo || <span style={{ color: '#cbd5e1' }}>Sin vehículo</span>}
                      {(r.combustible_tipo || kmL) && (
                        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                          ⛽ {combLabel}
                          {kmL && ` · ${kmL} km/l`}
                          {r.tarifa_facturada_por_viaje && ` · $${r.tarifa_facturada_por_viaje}/viaje`}
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {r.stats.hoy > 0
                        ? <span className="badge badge-blue">{r.stats.hoy}</span>
                        : <span style={{ color: '#cbd5e1' }}>—</span>}
                    </td>
                    <td style={{ textAlign: 'center', fontSize: 13 }}>
                      <div>{r.stats.total} <span style={{ color: '#94a3b8', fontSize: 11 }}>total</span></div>
                      {r.stats.entregadas > 0 && (
                        <div style={{ fontSize: 10, color: '#10B981' }}>{r.stats.entregadas} entregadas</div>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', fontSize: 13 }}>
                      {r.km_mes > 0 ? (
                        <>
                          <div style={{ fontWeight: 700, color: '#1a3461' }}>{r.km_mes.toFixed(1)} km</div>
                          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                            <span title="Tienda Limpack">🏪 {r.km_tienda.toFixed(1)}</span>
                            <span style={{ margin: '0 4px', color: '#e2e8f0' }}>·</span>
                            <span title="MercadoLibre Flex" style={{ color: r.km_ml > 0 ? '#3730A3' : '#cbd5e1' }}>ML {r.km_ml.toFixed(1)}</span>
                          </div>
                          <div style={{ fontSize: 10, color: '#94a3b8' }}>
                            ≈ ${Math.round(r.costo_combustible_mes || 0)} {r.combustible_tipo === 'gasoil' ? 'gasoil' : 'nafta'}
                          </div>
                          {r.costo_facturado_mes > 0 && (
                            <div style={{ fontSize: 10, color: '#b45309' }}>
                              + ${Math.round(r.costo_facturado_mes)} facturado
                            </div>
                          )}
                        </>
                      ) : (
                        <span style={{ color: '#cbd5e1' }}>—</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button onClick={() => setEditRep(r)} className="btn-mini" title="Editar datos">
                        ✏️
                      </button>
                      <button onClick={() => reenviarLink(r)} className="btn-mini" style={{ marginLeft: 6 }} title="Generar nuevo magic link">
                        🔗
                      </button>
                      <button onClick={() => toggleActivo(r)} className="btn-mini" style={{ marginLeft: 6 }} title={r.activo ? 'Pausar repartidor' : 'Reactivar'}>
                        {r.activo ? '⏸' : '▶'}
                      </button>
                    </td>
                  </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {modalOpen && (
          <ModalInvitar
            onClose={() => setModalOpen(false)}
            onSuccess={() => { setModalOpen(false); cargar() }}
          />
        )}

        {linkModal && (
          <ModalLink
            rep={linkModal.rep}
            link={linkModal.link}
            onClose={() => setLinkModal(null)}
          />
        )}

        {editRep && (
          <ModalEditar
            rep={editRep}
            onClose={() => setEditRep(null)}
            onSuccess={() => { setEditRep(null); cargar() }}
          />
        )}
      </div>

      <style>{`
        /* Resumen operativo — 1 fila compacta con las métricas clave del mes */
        .rep-resumen {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 12px;
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 14px 18px;
          margin-bottom: 16px;
        }
        .rep-resumen-item { padding: 0 4px; border-right: 1px solid #f1f5f9; }
        .rep-resumen-item:last-child { border-right: 0; }
        .rep-resumen-label {
          font-size: 10.5px; font-weight: 700; color: #64748b;
          text-transform: uppercase; letter-spacing: .04em; margin-bottom: 4px;
        }
        .rep-resumen-value {
          font-size: 22px; font-weight: 800; color: #1a3461;
          font-variant-numeric: tabular-nums; line-height: 1;
        }
        .rep-resumen-sub { font-size: 12px; font-weight: 500; color: #94a3b8; }

        .btn-primary {
          background: #1a3461; color: #fff; border: none; padding: 10px 18px;
          border-radius: 10px; font-weight: 700; cursor: pointer; font-size: 14px;
        }
        .btn-primary:hover { background: #15294d; }
        .btn-mini {
          padding: 5px 10px; font-size: 12px; font-weight: 600; cursor: pointer;
          background: #f1f5f9; border: 1px solid #e2e8f0; border-radius: 6px; color: #475569;
        }
        .btn-mini:hover { background: #e2e8f0; }
        .alert-error {
          padding: 12px; background: #fee2e2; border: 1px solid #fca5a5;
          border-radius: 8px; color: #991b1b; margin-bottom: 12px;
        }
        .badge {
          display: inline-block; min-width: 22px; padding: 2px 8px;
          border-radius: 999px; font-size: 12px; font-weight: 700;
        }
        .badge-blue { background: #dbeafe; color: #1e40af; }
        .rep-table { width: 100%; border-collapse: collapse; font-size: 14px; }
        .rep-table thead th {
          text-align: left; padding: 12px 14px; background: #f8fafc;
          border-bottom: 1px solid #e5e7eb; font-size: 12px;
          text-transform: uppercase; color: #6b7280; font-weight: 700; letter-spacing: .03em;
        }
        .rep-table tbody td { padding: 12px 14px; border-bottom: 1px solid #f1f5f9; vertical-align: top; }
        .rep-table tbody tr:hover { background: #f8fafc; }
        .rep-table tbody tr.row-link { cursor: pointer; }
        .rep-table tbody tr.row-link:hover { background: #eef2f7; }
        .rep-table tbody tr.inactivo { opacity: 0.55; }
        .ap-filtros { display: flex; gap: 6px; flex-wrap: wrap; }
        .ap-filtro {
          padding: 8px 14px; border: 1px solid #e5e7eb; background: #fff;
          border-radius: 999px; cursor: pointer; font-size: 13px; font-weight: 600; color: #475569;
          display: inline-flex; align-items: center; gap: 6px;
        }
        .ap-filtro em { font-style: normal; opacity: .7; font-size: 11px; }
        .ap-filtro.is-active { background: #1a3461; color: #fff; border-color: #1a3461; }
        .ap-filtro:hover:not(.is-active) { background: #f1f5f9; }
        .ap-loading { padding: 40px; text-align: center; color: #6b7280; }
        .ap-empty { padding: 60px 20px; text-align: center; color: #6b7280; }
        .ap-empty h3 { color: #1a3461; margin: 16px 0 8px; }

        /* Modales — estilos globales compartidos entre ModalInvitar, ModalLink, ModalEditar */
        .modal-backdrop {
          position: fixed; inset: 0; background: rgba(15, 23, 42, .55);
          display: flex; align-items: center; justify-content: center; z-index: 1000;
          padding: 16px; backdrop-filter: blur(2px);
        }
        .modal {
          background: #fff; border-radius: 16px; padding: 28px 24px 24px;
          max-width: 460px; width: 100%; max-height: 90vh; overflow-y: auto;
          position: relative; box-shadow: 0 24px 60px rgba(15, 23, 42, .35);
        }
        .modal-close {
          position: absolute; top: 14px; right: 14px;
          background: transparent; border: none; font-size: 18px; cursor: pointer; color: #94a3b8;
          width: 32px; height: 32px; border-radius: 50%;
          display: inline-flex; align-items: center; justify-content: center;
        }
        .modal-close:hover { background: #f1f5f9; color: #475569; }
        .modal h2 { margin: 0 0 6px; color: #1a3461; font-size: 20px; }
        .modal-sub {
          font-size: 13px; color: #64748b; margin: 0 0 18px; line-height: 1.45;
        }
        .modal-actions {
          display: flex; gap: 8px; justify-content: flex-end; margin-top: 22px;
          padding-top: 16px; border-top: 1px solid #f1f5f9;
        }

        /* Botón WhatsApp grande */
        .btn-whatsapp {
          width: 100%; display: flex; align-items: center; gap: 12px;
          background: #25d366; color: #fff; border: none;
          padding: 14px 18px; border-radius: 12px;
          font-weight: 700; font-size: 15px; cursor: pointer;
          box-shadow: 0 4px 12px rgba(37, 211, 102, .35);
          transition: background .15s, transform .1s, box-shadow .15s;
          text-align: left;
        }
        .btn-whatsapp:hover { background: #20bf5b; box-shadow: 0 6px 16px rgba(37, 211, 102, .45); }
        .btn-whatsapp:active { transform: translateY(1px); }
        .btn-wa-icon {
          width: 36px; height: 36px; flex-shrink: 0;
          background: rgba(255, 255, 255, .2); border-radius: 50%;
          display: flex; align-items: center; justify-content: center; font-size: 18px;
        }
        .btn-wa-arrow { margin-left: auto; opacity: .85; font-size: 20px; }

        /* Tel chip que va abajo del botón */
        .tel-chip {
          display: flex; align-items: center; gap: 6px; margin-top: 10px;
          font-size: 12px; color: #64748b;
        }
        .tel-chip strong { color: #334155; }

        /* Botón secundario para copiar */
        .btn-copy {
          width: 100%; padding: 11px 14px; margin-top: 12px;
          background: #f8fafc; color: #475569; border: 1px solid #e2e8f0;
          border-radius: 10px; cursor: pointer; font-size: 13px; font-weight: 600;
          display: inline-flex; align-items: center; justify-content: center; gap: 6px;
        }
        .btn-copy:hover { background: #f1f5f9; border-color: #cbd5e1; }

        /* Banner verde de éxito dentro del modal de invitar */
        .modal-success {
          padding: 14px 16px; background: #dcfce7; border: 1px solid #86efac;
          border-radius: 10px; margin-bottom: 18px;
        }
        .modal-success-title { font-weight: 700; color: #166534; font-size: 14px; }
        .modal-success-sub { font-size: 13px; color: #15803d; margin-top: 4px; }

        /* Disclosure "Ver link completo" */
        .link-disclosure { margin-top: 14px; }
        .link-disclosure summary {
          font-size: 12px; color: #6b7280; cursor: pointer;
          padding: 4px 0; list-style: none;
        }
        .link-disclosure summary::-webkit-details-marker { display: none; }
        .link-disclosure summary::before { content: '▸ '; }
        .link-disclosure[open] summary::before { content: '▾ '; }
        .link-disclosure-content {
          margin-top: 8px; padding: 10px 12px; background: #f8fafc;
          border: 1px solid #e2e8f0; border-radius: 8px;
          font-size: 11px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          word-break: break-all; color: #475569; line-height: 1.5;
        }
      `}</style>
    </AdminLayout>
  )
}

function ModalInvitar({ onClose, onSuccess }) {
  const [form, setForm] = useState({
    nombre: '', email: '', telefono: '', vehiculo: '', zona: '', notas: '',
  })
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState(null)
  const [resultado, setResultado] = useState(null)

  function up(k) { return (e) => setForm({ ...form, [k]: e.target.value }) }

  async function enviar(e) {
    e.preventDefault()
    setError(null)
    setEnviando(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(FN_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form),
      })
      const j = await res.json()
      if (!res.ok) throw new Error(j.error || 'Error')
      setResultado(j)
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setEnviando(false)
    }
  }

  async function copiarLink() {
    if (resultado?.action_link) {
      await navigator.clipboard.writeText(resultado.action_link)
      alert('✅ Link copiado')
    }
  }

  function abrirWhatsApp() {
    if (!resultado?.action_link) return
    const tel = (form.telefono || '').replace(/[^\d]/g, '')
    const nombre = form.nombre || 'che'
    const mensaje = `Hola ${nombre}! 👋\n\nTe invito al panel de Limpack como repartidor.\n\nEntrá con este link y vas a estar adentro:\n${resultado.action_link}\n\nCualquier cosa me avisás.`
    // Si tiene teléfono, abrimos chat directo. Si no, abrimos selector de contacto.
    const url = tel
      ? `https://wa.me/${tel}?text=${encodeURIComponent(mensaje)}`
      : `https://wa.me/?text=${encodeURIComponent(mensaje)}`
    window.open(url, '_blank', 'noopener')
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <h2>{!resultado ? '🚐 Invitar repartidor' : '🎉 Listo'}</h2>

        {!resultado ? (
          <form onSubmit={enviar}>
            <Campo label="Nombre completo" required>
              <input value={form.nombre} onChange={up('nombre')} required
                placeholder="Ej: Roberto Jaime" />
            </Campo>
            <Campo label="Email" required>
              <input type="email" value={form.email} onChange={up('email')} required
                placeholder="roberto@limpack.uy" />
            </Campo>
            <Campo label="Teléfono">
              <input type="tel" value={form.telefono} onChange={up('telefono')}
                placeholder="+598 99 123 456" />
            </Campo>
            <Campo label="Vehículo">
              <input value={form.vehiculo} onChange={up('vehiculo')}
                placeholder="Moto Honda 125 — matrícula AAA 123" />
            </Campo>
            <Campo label="Zona habitual">
              <input value={form.zona} onChange={up('zona')}
                placeholder="Pocitos, Punta Carretas, Villa Biarritz" />
            </Campo>
            <Campo label="Notas internas">
              <textarea value={form.notas} onChange={up('notas')} rows={2}
                placeholder="Días disponibles, turno, alergia a perros, etc." />
            </Campo>

            {error && <div className="alert-error" style={{ marginTop: 12 }}>{error}</div>}

            <div className="modal-actions">
              <button type="button" onClick={onClose} className="btn-mini" disabled={enviando}>
                Cancelar
              </button>
              <button type="submit" className="btn-primary" disabled={enviando}>
                {enviando ? 'Creando…' : '✓ Invitar y generar link'}
              </button>
            </div>
          </form>
        ) : (
          <div>
            <div className="modal-success">
              <div className="modal-success-title">✅ {form.nombre} ya es repartidor</div>
              <div className="modal-success-sub">Ahora mandale el link para que pueda entrar.</div>
            </div>

            {resultado.action_link && (
              <>
                <button onClick={abrirWhatsApp} className="btn-whatsapp">
                  <span className="btn-wa-icon">💬</span>
                  <span>Enviar por WhatsApp</span>
                  <span className="btn-wa-arrow">→</span>
                </button>
                {form.telefono && (
                  <div className="tel-chip">
                    <span>📱</span>
                    <span>Se abre chat con <strong>{form.telefono}</strong></span>
                  </div>
                )}

                <button onClick={copiarLink} className="btn-copy">📋 Copiar link</button>

                <details className="link-disclosure">
                  <summary>Ver link completo</summary>
                  <div className="link-disclosure-content">{resultado.action_link}</div>
                </details>

                <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 14, lineHeight: 1.5 }}>
                  El link es de un solo uso. Si lo necesita de nuevo, click en <strong>🔗 Link</strong> en la fila.
                </p>
              </>
            )}

            <div className="modal-actions">
              <button onClick={onSuccess} className="btn-mini">Cerrar</button>
            </div>
          </div>
        )}
      </div>

    </div>
  )
}

function ModalEditar({ rep, onClose, onSuccess }) {
  const [form, setForm] = useState({
    nombre: rep.nombre || '',
    telefono: rep.phone || '',
    vehiculo: rep.vehiculo || '',
    zona: rep.zona || '',
    notas: rep.notas || '',
    origen_lat: rep.origen_lat ?? '',
    origen_lng: rep.origen_lng ?? '',
    origen_descripcion: rep.origen_descripcion || '',
    // Combustible + tarifa
    combustible_tipo: rep.combustible_tipo || 'nafta',
    km_por_litro_override: rep.km_por_litro_override ?? '',
    precio_litro_override: rep.precio_litro_override ?? '',
    tarifa_facturada_por_viaje: rep.tarifa_facturada_por_viaje ?? '',
    regresa_a_origen: rep.regresa_a_origen !== false, // default true
  })
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState(null)

  function up(k) { return (e) => setForm({ ...form, [k]: e.target.value }) }

  async function guardar(e) {
    e.preventDefault()
    setError(null)
    setGuardando(true)
    try {
      // 1) Actualizar profile (nombre + phone). Email NO se toca.
      const { error: pErr } = await supabase
        .from('profiles')
        .update({
          nombre: form.nombre,
          phone: form.telefono || null,
        })
        .eq('id', rep.id)
      if (pErr) throw pErr

      // 2) Actualizar repartidor (vehículo, zona, notas, origen para km)
      const parseNum = (v) => {
        if (v === '' || v == null) return null
        const n = Number(v)
        return Number.isFinite(n) ? n : null
      }
      const { error: rErr } = await supabase
        .from('repartidores')
        .update({
          vehiculo: form.vehiculo || null,
          zona: form.zona || null,
          notas: form.notas || null,
          origen_lat: parseNum(form.origen_lat),
          origen_lng: parseNum(form.origen_lng),
          origen_descripcion: form.origen_descripcion || null,
          combustible_tipo: form.combustible_tipo || 'nafta',
          km_por_litro_override: parseNum(form.km_por_litro_override),
          precio_litro_override: parseNum(form.precio_litro_override),
          tarifa_facturada_por_viaje: parseNum(form.tarifa_facturada_por_viaje),
          regresa_a_origen: form.regresa_a_origen,
        })
        .eq('id', rep.id)
      if (rErr) throw rErr

      onSuccess()
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <h2>✏️ Editar repartidor</h2>

        <form onSubmit={guardar}>
          <Campo label="Email">
            <input type="email" value={rep.email || ''} disabled
              style={{ background: '#f8fafc', color: '#94a3b8', cursor: 'not-allowed' }} />
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
              El email no se puede editar. Si necesitás cambiarlo, hay que crear un user nuevo.
            </div>
          </Campo>
          <Campo label="Nombre completo" required>
            <input value={form.nombre} onChange={up('nombre')} required />
          </Campo>
          <Campo label="Teléfono">
            <input type="tel" value={form.telefono} onChange={up('telefono')}
              placeholder="+598 99 123 456" />
          </Campo>
          <Campo label="Vehículo">
            <input value={form.vehiculo} onChange={up('vehiculo')}
              placeholder="Moto Honda 125 — matrícula AAA 123" />
          </Campo>
          <Campo label="Zona habitual">
            <input value={form.zona} onChange={up('zona')}
              placeholder="Pocitos, Punta Carretas, Villa Biarritz" />
          </Campo>
          <Campo label="Notas internas">
            <textarea value={form.notas} onChange={up('notas')} rows={2}
              placeholder="Días disponibles, turno, alergia a perros, etc." />
          </Campo>

          <div style={{ marginTop: 18, marginBottom: 6, padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#1a3461', textTransform: 'uppercase', letterSpacing: '.05em' }}>
              ⛽ Combustible y tarifa
            </div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 3, lineHeight: 1.4 }}>
              Define cómo se calcula el costo real por km. Si dejás km/l y precio vacíos, se usan los globales
              (nafta $88.67/l · gasoil $58.68/l · 10 km/l). Para tercerizados con tarifa fija por viaje, cargala aparte.
            </div>
          </div>
          <Campo label="Tipo de combustible">
            <select value={form.combustible_tipo} onChange={up('combustible_tipo')}
              style={{ width: '100%', padding: '9px 11px', borderRadius: 8, border: '1.5px solid #E2E8F0', fontSize: 14, background: '#fff' }}>
              <option value="nafta">Nafta (Súper 95)</option>
              <option value="gasoil">Gasoil (50S)</option>
              <option value="ninguno">Ninguno (tercerizado)</option>
            </select>
          </Campo>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Campo label="km por litro (real)">
              <input type="number" step="0.1" min="0" value={form.km_por_litro_override}
                onChange={up('km_por_litro_override')} placeholder="10" />
            </Campo>
            <Campo label="$ por litro (override)">
              <input type="number" step="0.01" min="0" value={form.precio_litro_override}
                onChange={up('precio_litro_override')} placeholder="usa global" />
            </Campo>
          </div>
          <Campo label="Tarifa facturada por viaje (solo tercerizados)">
            <input type="number" step="0.01" min="0" value={form.tarifa_facturada_por_viaje}
              onChange={up('tarifa_facturada_por_viaje')} placeholder="ej: 250 (lo que él te cobra por viaje)" />
          </Campo>
          <label style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
            background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', marginBottom: 12,
          }}>
            <input type="checkbox" checked={form.regresa_a_origen}
              onChange={e => setForm({ ...form, regresa_a_origen: e.target.checked })}
              style={{ width: 18, height: 18, cursor: 'pointer' }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1a3461' }}>Regresa al depósito al final del día</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>
                Si NO regresa (típico de tercerizados como Envíos Xpress), no se suma el km del regreso al depósito.
              </div>
            </div>
          </label>

          <div style={{ marginTop: 18, marginBottom: 6, padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#1a3461', textTransform: 'uppercase', letterSpacing: '.05em' }}>
              🚗 Origen de la ruta (opcional)
            </div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 3, lineHeight: 1.4 }}>
              Punto donde arranca cada día. Si lo dejás vacío, se usa el depósito Cavia 2858 como origen.
              Los km del primer envío se miden desde acá.
            </div>
          </div>
          <Campo label="Descripción del origen">
            <input value={form.origen_descripcion} onChange={up('origen_descripcion')}
              placeholder="Casa - Pocitos" />
          </Campo>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Campo label="Latitud">
              <input type="number" step="any" value={form.origen_lat} onChange={up('origen_lat')}
                placeholder="-34.9089" />
            </Campo>
            <Campo label="Longitud">
              <input type="number" step="any" value={form.origen_lng} onChange={up('origen_lng')}
                placeholder="-56.1552" />
            </Campo>
          </div>

          {error && <div className="alert-error" style={{ marginTop: 12 }}>{error}</div>}

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-mini" disabled={guardando}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={guardando}>
              {guardando ? 'Guardando…' : '✓ Guardar cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function ModalLink({ rep, link, onClose }) {
  function abrirWhatsApp() {
    const tel = (rep.phone || '').replace(/[^\d]/g, '')
    const nombre = rep.nombre || 'che'
    const mensaje = `Hola ${nombre}! 👋\n\nAcá te paso el link de acceso al panel de Limpack:\n${link}\n\nCualquier cosa me avisás.`
    const url = tel
      ? `https://wa.me/${tel}?text=${encodeURIComponent(mensaje)}`
      : `https://wa.me/?text=${encodeURIComponent(mensaje)}`
    window.open(url, '_blank', 'noopener')
  }

  async function copiar() {
    await navigator.clipboard.writeText(link)
    alert('✅ Link copiado')
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <h2>Link para {rep.nombre}</h2>
        <p className="modal-sub">
          Nuevo link de acceso generado. Mandáselo por WhatsApp o copialo para enviarlo por otro lado.
        </p>

        <button onClick={abrirWhatsApp} className="btn-whatsapp">
          <span className="btn-wa-icon">💬</span>
          <span>Enviar por WhatsApp</span>
          <span className="btn-wa-arrow">→</span>
        </button>
        {rep.phone && (
          <div className="tel-chip">
            <span>📱</span>
            <span>Se abre chat con <strong>{rep.phone}</strong></span>
          </div>
        )}

        <button onClick={copiar} className="btn-copy">📋 Copiar link</button>

        <details className="link-disclosure">
          <summary>Ver link completo</summary>
          <div className="link-disclosure-content">{link}</div>
        </details>

        <div className="modal-actions">
          <button onClick={onClose} className="btn-mini">Cerrar</button>
        </div>
      </div>
    </div>
  )
}

function Campo({ label, required, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: 4 }}>
        {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
      </label>
      <div className="field-wrap">{children}</div>
      <style>{`
        .field-wrap input, .field-wrap textarea {
          width: 100%; padding: 10px 12px;
          border: 1px solid #d1d5db; border-radius: 8px; font-size: 14px;
          font-family: inherit; box-sizing: border-box;
        }
        .field-wrap input:focus, .field-wrap textarea:focus {
          outline: none; border-color: #1a3461; box-shadow: 0 0 0 3px rgba(26,52,97,.1);
        }
      `}</style>
    </div>
  )
}
